import { Injectable } from '@angular/core';

import type {
  BaseEvent,
  MessagesSnapshotEvent,
  ReasoningMessageChunkEvent,
  ReasoningMessageContentEvent,
  ReasoningMessageEndEvent,
  ReasoningMessageStartEvent,
  RunErrorEvent,
  StateDeltaEvent,
  StateSnapshotEvent,
  TextMessageChunkEvent,
  TextMessageContentEvent,
  TextMessageEndEvent,
  TextMessageStartEvent,
  ToolCallArgsEvent,
  ToolCallChunkEvent,
  ToolCallEndEvent,
  ToolCallResultEvent,
  ToolCallStartEvent,
} from '@ag-ui/core';
import { EventType } from '@ag-ui/core';

import { applyStateDelta } from '@core/tellent-assistant-util';

import type { TellentAssistantStore } from './tellent-assistant.store';

@Injectable({ providedIn: 'root' })
export class TellentAssistantEventAdapter {
  public apply(event: BaseEvent, store: TellentAssistantStore): void {
    switch (event.type) {
      case EventType.RUN_STARTED:
        store.markRunStart();
        break;
      case EventType.TEXT_MESSAGE_START:
        store.startTextPart((<TextMessageStartEvent>event).messageId);
        break;
      case EventType.TEXT_MESSAGE_CONTENT:
        this.applyTextContent(<TextMessageContentEvent>event, store);
        break;
      case EventType.TEXT_MESSAGE_END:
        store.finishTextPart((<TextMessageEndEvent>event).messageId);
        break;
      case EventType.TEXT_MESSAGE_CHUNK:
        this.applyTextChunk(<TextMessageChunkEvent>event, store);
        break;
      case EventType.REASONING_MESSAGE_START:
        store.startReasoningPart((<ReasoningMessageStartEvent>event).messageId);
        break;
      case EventType.REASONING_MESSAGE_CONTENT:
        this.applyReasoningContent(<ReasoningMessageContentEvent>event, store);
        break;
      case EventType.REASONING_MESSAGE_END:
        store.finishReasoningPart((<ReasoningMessageEndEvent>event).messageId);
        break;
      case EventType.REASONING_MESSAGE_CHUNK:
        this.applyReasoningChunk(<ReasoningMessageChunkEvent>event, store);
        break;
      case EventType.TOOL_CALL_START:
        this.applyToolCallStart(<ToolCallStartEvent>event, store);
        break;
      case EventType.TOOL_CALL_ARGS:
        this.applyToolCallArgs(<ToolCallArgsEvent>event, store);
        break;
      case EventType.TOOL_CALL_END:
        store.finishToolCall((<ToolCallEndEvent>event).toolCallId);
        break;
      case EventType.TOOL_CALL_RESULT:
        this.applyToolCallResult(<ToolCallResultEvent>event, store);
        break;
      case EventType.TOOL_CALL_CHUNK:
        this.applyToolCallChunk(<ToolCallChunkEvent>event, store);
        break;
      case EventType.STATE_SNAPSHOT:
        store.replaceState((<StateSnapshotEvent>event).snapshot);
        break;
      case EventType.STATE_DELTA:
        this.applyStateDeltaEvent(<StateDeltaEvent>event, store);
        break;
      case EventType.MESSAGES_SNAPSHOT:
        void (<MessagesSnapshotEvent>event).messages;
        store.replaceMessages([]);
        break;
      case EventType.CUSTOM:
        // No-op: interrupts and other control signals travel via tool-result
        // sentinels (see docs/core/interrupts/general.md). CUSTOM events are
        // retained on the wire for telemetry/observability but do not drive UI.
        break;
      case EventType.RUN_FINISHED:
        store.finalizeOpenParts();
        store.markRunEnd();
        break;
      case EventType.RUN_ERROR:
        store.markRunError((<RunErrorEvent>event).message);
        break;
      default:
        break;
    }
  }

  private applyTextContent(e: TextMessageContentEvent, store: TellentAssistantStore): void {
    store.appendTextDelta(e.messageId, e.delta);
  }

  private applyReasoningContent(
    e: ReasoningMessageContentEvent,
    store: TellentAssistantStore,
  ): void {
    store.appendReasoningDelta(e.messageId, e.delta);
  }

  private applyToolCallStart(e: ToolCallStartEvent, store: TellentAssistantStore): void {
    store.startToolCall(e.toolCallId, e.toolCallName);
  }

  private applyToolCallArgs(e: ToolCallArgsEvent, store: TellentAssistantStore): void {
    store.appendToolArgs(e.toolCallId, e.delta);
  }

  private applyToolCallResult(e: ToolCallResultEvent, store: TellentAssistantStore): void {
    store.appendToolResult(e.toolCallId, e.content);
  }

  private applyStateDeltaEvent(e: StateDeltaEvent, store: TellentAssistantStore): void {
    store.updateState(prev => applyStateDelta(prev, e.delta));
  }

  private applyTextChunk(e: TextMessageChunkEvent, store: TellentAssistantStore): void {
    if (!e.messageId) return;
    store.startTextPart(e.messageId);
    if (e.delta) store.appendTextDelta(e.messageId, e.delta);
  }

  private applyReasoningChunk(e: ReasoningMessageChunkEvent, store: TellentAssistantStore): void {
    if (!e.messageId) return;
    store.startReasoningPart(e.messageId);
    if (e.delta) store.appendReasoningDelta(e.messageId, e.delta);
  }

  private applyToolCallChunk(e: ToolCallChunkEvent, store: TellentAssistantStore): void {
    if (!e.toolCallId) return;
    store.startToolCall(e.toolCallId, e.toolCallName ?? '');
    if (e.delta) store.appendToolArgs(e.toolCallId, e.delta);
  }
}
